import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon       from '@mui/icons-material/Close';
import PersonIcon      from '@mui/icons-material/Person';
import SchoolIcon      from '@mui/icons-material/School';
import PhoneIcon       from '@mui/icons-material/Phone';
import EmailIcon       from '@mui/icons-material/Email';
import CakeIcon        from '@mui/icons-material/Cake';
import WorkIcon        from '@mui/icons-material/Work';
import PeopleIcon      from '@mui/icons-material/People';
import ArrowBackIcon   from '@mui/icons-material/ArrowBack';
import axios           from 'axios';

// ─── 型定義 ─────────────────────────────────────────────────

interface ParticipantItem {
  id:              number;
  user_id:         number;
  user_name:       string;
  furigana:        string | null;
  user_email:      string;
  university:      string | null;
  faculty:         string | null;
  gender:          string | null;
  phone:           string | null;
  birth_date:      string | null;
  graduation_year: number | null;
  status:          string;
  applied_at:      string;
}

interface ParticipantProfile {
  id:                number;
  full_name:         string;
  furigana:          string | null;
  email:             string;
  gender:            string | null;
  phone:             string | null;
  birth_date:        string | null;
  university:        string | null;
  faculty:           string | null;
  graduation_year:   number | null;
  graduation_status: string | null;
  career_goal:       string | null;
  bio:               string | null;
  avatar_url:        string | null;
  applied_at:        string;
}

export interface EventParticipantsDialogProps {
  open:     boolean;
  onClose:  () => void;
  eventId:  number;
  eventTitle: string;
}

// ─── ユーティリティ ──────────────────────────────────────────

const GENDER_LABELS: Record<string, string> = {
  male:   '男性',
  female: '女性',
  other:  'その他',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });

const formatDatetime = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const calcAge = (birthDate: string | null): string => {
  if (!birthDate) return '';
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return ` (${age}歳)`;
};

const initials = (name: string) => name.slice(0, 2);

// ─── InfoRow サブコンポーネント ──────────────────────────────

interface InfoRowProps {
  icon?:    React.ReactNode;
  label:    string;
  value?:   string | React.ReactNode;
  isEmpty?: boolean;
}
const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, isEmpty }) => (
  <Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
    {icon
      ? <Box sx={{ color: 'primary.main', mt: '2px', flexShrink: 0, fontSize: 18 }}>{icon}</Box>
      : <Box sx={{ width: 18, flexShrink: 0 }} />
    }
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.25} fontWeight={500}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        color={isEmpty ? 'text.secondary' : 'text.primary'}
        fontStyle={isEmpty ? 'italic' : 'normal'}
      >
        {value ?? '未設定'}
      </Typography>
    </Box>
  </Box>
);

// ─── 参加者リスト ────────────────────────────────────────────

interface ParticipantListProps {
  eventId:    number;
  selected:   number | null;
  onSelect:   (userId: number) => void;
}

const ParticipantList: React.FC<ParticipantListProps> = ({ eventId, selected, onSelect }) => {
  const [participants, setParticipants] = useState<ParticipantItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`/api/events/${eventId}/participants`, { withCredentials: true })
      .then((res) => { if (!cancelled) setParticipants(res.data.data.participants ?? []); })
      .catch(() => { if (!cancelled) setError('参加者一覧の取得に失敗しました'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId]);

  if (loading) return (
    <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
  );
  if (error) return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
  if (participants.length === 0) return (
    <Box p={3} textAlign="center">
      <PeopleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">参加者はいません</Typography>
    </Box>
  );

  return (
    <Box>
      {participants.map((p) => (
        <Box
          key={p.user_id}
          onClick={() => onSelect(p.user_id)}
          sx={{
            p: 1.5,
            cursor: 'pointer',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: selected === p.user_id ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: selected === p.user_id ? 'action.selected' : 'action.hover' },
            transition: 'background-color 0.15s',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 36, height: 36, fontSize: '0.8rem', bgcolor: 'primary.light' }}>
              {initials(p.user_name || '?')}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {p.user_name}
                {p.furigana && (
                  <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                    （{p.furigana}）
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {p.university ?? '大学未設定'}{p.faculty ? ` / ${p.faculty}` : ''}
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block">
                申込: {formatDatetime(p.applied_at)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      ))}
    </Box>
  );
};

// ─── 学生プロフィールパネル ──────────────────────────────────

interface ProfilePanelProps {
  eventId: number;
  userId:  number;
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({ eventId, userId }) => {
  const [profile, setProfile] = useState<ParticipantProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProfile(null);
    axios.get(`/api/events/${eventId}/participants/${userId}/profile`, { withCredentials: true })
      .then((res) => { if (!cancelled) setProfile(res.data.data); })
      .catch(() => { if (!cancelled) setError('プロフィールの取得に失敗しました'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, userId]);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%" py={6}>
      <CircularProgress size={28} />
    </Box>
  );
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!profile) return null;

  return (
    <Box p={2.5}>
      {/* ヘッダー */}
      <Stack direction="row" spacing={2} alignItems="center" mb={2.5}>
        <Avatar sx={{ width: 52, height: 52, fontSize: '1.1rem', bgcolor: 'primary.main' }}>
          {initials(profile.full_name || '?')}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {profile.full_name}
          </Typography>
          {profile.furigana && (
            <Typography variant="body2" color="text.secondary">{profile.furigana}</Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            申込日時: {formatDatetime(profile.applied_at)}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* 基本情報 */}
      <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1.5}>基本情報</Typography>
      <InfoRow icon={<EmailIcon sx={{ fontSize: 16 }} />} label="メールアドレス" value={profile.email} />
      <InfoRow icon={<PhoneIcon sx={{ fontSize: 16 }} />} label="電話番号" value={profile.phone ?? undefined} isEmpty={!profile.phone} />
      <InfoRow icon={<CakeIcon  sx={{ fontSize: 16 }} />}
        label="生年月日"
        value={profile.birth_date ? `${formatDate(profile.birth_date)}${calcAge(profile.birth_date)}` : undefined}
        isEmpty={!profile.birth_date}
      />
      <InfoRow
        label="性別"
        value={profile.gender ? (GENDER_LABELS[profile.gender] ?? profile.gender) : undefined}
        isEmpty={!profile.gender}
      />

      <Divider sx={{ mb: 2, mt: 0.5 }} />

      {/* 学歴 */}
      <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1.5}>学歴</Typography>
      <InfoRow icon={<SchoolIcon sx={{ fontSize: 16 }} />} label="大学" value={profile.university ?? undefined} isEmpty={!profile.university} />
      <InfoRow label="学部・学科" value={profile.faculty ?? undefined} isEmpty={!profile.faculty} />
      {profile.graduation_year && (
        <InfoRow label="卒業予定年度" value={`${profile.graduation_year}年卒`} />
      )}
      {profile.graduation_status && (
        <InfoRow label="卒業状況" value={profile.graduation_status} />
      )}

      {/* キャリア・自己PR */}
      {(profile.career_goal || profile.bio) && (
        <>
          <Divider sx={{ mb: 2, mt: 0.5 }} />
          <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1.5}>キャリア・自己PR</Typography>
          {profile.career_goal && (
            <InfoRow icon={<WorkIcon sx={{ fontSize: 16 }} />} label="キャリア目標" value={
              <Typography variant="body2" whiteSpace="pre-wrap">{profile.career_goal}</Typography>
            } />
          )}
          {profile.bio && (
            <InfoRow label="自己PR" value={
              <Typography variant="body2" whiteSpace="pre-wrap">{profile.bio}</Typography>
            } />
          )}
        </>
      )}
    </Box>
  );
};

// ─── メインダイアログ ────────────────────────────────────────

export const EventParticipantsDialog: React.FC<EventParticipantsDialogProps> = ({
  open,
  onClose,
  eventId,
  eventTitle,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [showProfile,    setShowProfile]    = useState(false);

  // ダイアログが閉じたらリセット
  useEffect(() => {
    if (!open) {
      setSelectedUserId(null);
      setShowProfile(false);
    }
  }, [open]);

  const handleSelect = (userId: number) => {
    setSelectedUserId(userId);
    if (isMobile) setShowProfile(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            {isMobile && showProfile && (
              <IconButton size="small" onClick={() => setShowProfile(false)}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isMobile && showProfile ? '学生プロフィール' : '参加者一覧'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {eventTitle}
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', height: isMobile ? '100%' : 520 }}>
        {/* 左カラム: 参加者リスト */}
        {(!isMobile || !showProfile) && (
          <Box
            sx={{
              width:     isMobile ? '100%' : 300,
              flexShrink: 0,
              borderRight: isMobile ? 'none' : '1px solid',
              borderColor: 'divider',
              overflowY: 'auto',
            }}
          >
            <ParticipantList
              eventId={eventId}
              selected={selectedUserId}
              onSelect={handleSelect}
            />
          </Box>
        )}

        {/* 右カラム: プロフィール詳細 */}
        {(!isMobile || showProfile) && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {selectedUserId ? (
              <ProfilePanel eventId={eventId} userId={selectedUserId} />
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" color="text.disabled">
                <PersonIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body2">左の一覧から学生を選択してください</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};
